<script setup>
import { ref , onMounted} from 'vue';
import AdminHeader from '../../AdminHeader.vue';
import { fetchLicenses } from '@/services/api';
import { useRouter } from 'vue-router';

const router = useRouter();
const licenses = ref([]);
const isLoading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(8);
const totalPages = ref(1);
const totalItems = ref(0);

onMounted(async () => {
    await getLicenses();
});

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
    });
}

const getLicenses = async () => {
    isLoading.value = true;
    const skip = (currentPage.value - 1) * itemsPerPage.value;
    const take = itemsPerPage.value;
    const response = await fetchLicenses(take, skip);
    if (response) {
        licenses.value = response.licenses;
        totalItems.value = response.count;
        totalPages.value = Math.ceil(totalItems.value / itemsPerPage.value);
    }
    isLoading.value = false;
}

const handleEdit = (id) => {
    localStorage.setItem('temp_editing_license_id', id);
    router.push({ name: 'license-save'});
}

const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        getLicenses();
    }
};

</script>

<template>
    <main class="p-5 bg-echoes-background-black">
        <AdminHeader>
            <template #title>
                Data Licenses
            </template>
            <template #description>
                Manage and organize data licenses
            </template>
            <template #actions>
                <button @click="router.push('/backoffice/licenses/save')" class="inline-flex items-center px-4 py-2.5 text-base font-medium rounded-xl bg-echoes-primary text-white hover:bg-echoes-primary/80 transition-colors duration-200">
                    <i class="mdi mdi-plus mr-2"></i>
                    Create License
                </button>
            </template>
        </AdminHeader>
        <!-- Table -->
        <div class="mb-4">
            <table class="min-w-full divide-y divide-echoes-border-light">
                <thead class="bg-echoes-border-light">
                    <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            Name
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            Created At
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            Allow Additions
                        </th>
                        <th scope="col" class="px-6 py-3 text-end text-xs font-medium text-white uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                    <template v-if="isLoading">
                        <tr v-for="i in 4" :key="i" class="bg-echoes-background-black/80 animate-pulse">
                            <td class="px-6 py-4">
                                <div class="flex items-center">
                                    <div class="ml-4">
                                        <div class="h-4 bg-echoes-background-gray rounded-full w-32 mb-2"></div>
                                    </div>
                                </div>
                            </td>
                            <td class="px-6 py-4">
                                <div class="flex items-center">
                                    <div class="ml-4">
                                        <div class="h-4 bg-echoes-background-gray rounded-full w-32 mb-2"></div>
                                        <div class="h-3 bg-echoes-background-gray rounded-full w-24"></div>
                                    </div>
                                </div>
                            </td>
                            <td class="px-6 py-4">
                                <div class="flex items-center">
                                    <div class="ml-4">
                                        <div class="h-3 bg-echoes-background-gray rounded-full w-24"></div>
                                    </div>
                                </div>
                            </td>
                            <td class="px-6 py-4">
                                <div class="flex items-center justify-end">
                                    <div class="h-5 w-16 bg-echoes-background-gray rounded"></div>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <template v-for="license in licenses" :key="license.id" v-else-if="licenses.length > 0">
                        <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-200">
                            <td class="px-6 py-4">
                                <div class="flex items-center">
                                    <div class="ml-4">
                                        <div class="text-sm font-medium text-white" :title="license.name">
                                            {{license.name}}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td class="px-6 py-4">
                                <div class="flex flex-col">
                                    <div class="text-sm font-medium text-white">
                                        {{ formatDate(license.created_at) }}
                                    </div>
                                    <div class="text-sm text-white/80">
                                        at {{ formatTime(license.created_at) }}
                                    </div>
                                </div>
                            </td>
                            <td class="px-6 py-4">
                                <div class="flex items-center">
                                    <div class="ml-4">
                                        <div class="text-sm text-white/80 text-center">{{ license.allow_additions ? 'Yes' : 'No' }}</div>
                                    </div>
                                </div>
                            </td>
                            <td class="px-6 py-4">
                                <div class="flex items-center justify-end space-x-4">
                                    <button 
                                        @click="handleEdit(license.id)"
                                        class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light">
                                    
                                    <i class="mdi mdi-pencil mr-2"></i>
                                    Edit/View
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <template v-else>
                        <tr>
                            <td colspan="4" class="px-6 py-4 text-center text-white">
                                <div class="flex items-center justify-center">
                                    <div class="text-sm font-medium text-white">
                                        No licenses found
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <!-- Pagination Footer -->
            <div class="bg-echoes-background-gray border-t rounded-b-xl border-echoes-border-light px-6 py-4">
                <div class="flex items-center justify-between">
                    <div class="flex items-center text-sm text-white/80">
                        <span>Showing</span>
                        <span class="font-medium mx-1">
                            {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                        </span>
                        <span>to</span>
                        <span class="font-medium mx-1">
                            {{ Math.min(currentPage * itemsPerPage, totalItems) }}
                        </span>
                        <span>of</span>
                        <span class="font-medium ml-1">{{ totalItems }}</span>
                    </div>
                    <div class="flex items-center space-x-2">
                        <button
                            @click="handlePageChange(currentPage - 1)"
                            :disabled="currentPage === 1"
                            class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <div class="flex items-center space-x-1">
                            <template v-for="page in totalPages" :key="page">
                                <button
                                    @click="handlePageChange(page)"
                                    :class="[
                                        'px-3 py-1 rounded-md text-sm font-medium',
                                        currentPage === page
                                            ? 'bg-echoes-primary text-white'
                                            : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                    ]"
                                >
                                    {{ page }}
                                </button>
                            </template>
                        </div>
                        <button
                            @click="handlePageChange(currentPage + 1)"
                            :disabled="currentPage === totalPages"
                            class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>